import React, { useEffect, useState } from "react";
import { Box, Button, Typography, IconButton, CircularProgress } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from "react-toastify";
import {
    GetAtsAgreementMastersHelper,
    AddAtsAgreementMasterHelper,
    DeleteAtsAgreementMasterHelper
} from "../../../ATS/helper/AtsAgreementHelper";
import { getImageSignedUrl } from "../../../Base/helper/baseFunctions";

const AgreementImageConfig = ({ masterType, label }) => {
    const [imageRecord, setImageRecord] = useState(null);
    const [imgUrl, setImgUrl] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [uploading, setUploading] = useState(false);

    const loadImage = () => {
        setIsLoading(true);
        GetAtsAgreementMastersHelper(masterType).then((res) => {
            if (res && !res.hasError && res.data?.data?.length > 0) {
                setImageRecord(res.data.data[0]);
            } else {
                setImageRecord(null);
            }
            setIsLoading(false);
        }).catch(() => {
            setIsLoading(false);
        });
    };

    useEffect(() => {
        loadImage();
    }, [masterType]);

    useEffect(() => {
        if (imageRecord && imageRecord.value) {
            const val = imageRecord.value;
            if (val.startsWith("data:") || val.startsWith("http://") || val.startsWith("https://")) {
                setImgUrl(val);
            } else {
                getImageSignedUrl(val).then((url) => {
                    setImgUrl(url || "");
                }).catch(() => {
                    setImgUrl("");
                });
            }
        } else {
            setImgUrl("");
        }
    }, [imageRecord]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        const reader = new FileReader();
        reader.onloadend = () => {
            AddAtsAgreementMasterHelper(masterType, file.name, reader.result).then((res) => {
                if (res && !res.hasError) {
                    toast.success(`${label} updated successfully`);
                    loadImage();
                } else {
                    toast.error("Failed to upload image");
                }
                setUploading(false);
            }).catch(() => {
                toast.error("An error occurred");
                setUploading(false);
            });
        };
        reader.readAsDataURL(file);
    };

    const handleDelete = () => {
        if (!imageRecord) return;
        setIsLoading(true);
        DeleteAtsAgreementMasterHelper(masterType, imageRecord.id).then((res) => {
            if (res && !res.hasError) {
                toast.success(`${label} deleted successfully`);
                setImageRecord(null);
            } else {
                toast.error("Failed to delete image");
            }
            setIsLoading(false);
        }).catch(() => {
            toast.error("An error occurred");
            setIsLoading(false);
        });
    };

    if (isLoading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2.5, border: "1px dashed #e2e8f0", borderRadius: "12px", bgcolor: "#f8fafc", display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", width: "100%" }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b" }}>
                        Default {label}
                    </Typography>
                    {!imageRecord && (
                        <Typography variant="caption" color="text.secondary">
                            No default image uploaded yet.
                        </Typography>
                    )}
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {imageRecord ? (
                        <IconButton 
                            color="error" 
                            onClick={handleDelete} 
                            title="Delete Image"
                            sx={{ 
                                bgcolor: "rgba(239, 68, 68, 0.08)", 
                                "&:hover": { bgcolor: "rgba(239, 68, 68, 0.16)" } 
                            }}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    ) : (
                        <Button
                            variant="outlined"
                            component="label"
                            startIcon={<CloudUploadIcon />}
                            size="small"
                            disabled={uploading}
                            sx={{ textTransform: "none", borderRadius: "6px", fontWeight: 600 }}
                        >
                            {uploading ? "Uploading..." : "Upload Image"}
                            <input type="file" hidden accept="image/*" onChange={handleFileChange} />
                        </Button>
                    )}
                </Box>
            </Box>

            {imageRecord && imgUrl && (
                <Box
                    sx={{
                        border: "1px solid #e2e8f0",
                        borderRadius: "8px",
                        overflow: "hidden",
                        maxWidth: "600px",
                        width: "100%",
                        height: "140px",
                        bgcolor: "#ffffff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 1,
                        boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)"
                    }}
                >
                    <img
                        src={imgUrl}
                        alt={label}
                        style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                            borderRadius: "4px"
                        }}
                    />
                </Box>
            )}
        </Box>
    );
};

export default AgreementImageConfig;
